/**
 * RepConnect - NPI Account Linking System
 *
 * Manages the connection between healthcare provider user accounts and their
 * official NPI registry profiles for verification and profile claiming.
 *
 * Copyright (c) 2025 RepConnect, LLC
 * 
 * Copyright (c) 2025 RepConnect, LLC
 */

import { logger } from './logger';

import { NPIAPI } from './npiAPI.js';

const NPI_LINKS_KEY = 'meetingsync_npi_links';
const VERIFICATION_ATTEMPTS_KEY = 'meetingsync_verification_attempts';

export const NPILinking = {

  /**
   * Get all NPI links
   * @returns {Array} All NPI account links
   */
  getAllLinks() {
    try {
      const links = localStorage.getItem(NPI_LINKS_KEY);
      return links ? JSON.parse(links) : [];
    } catch (error) {
      logger.error('Error getting NPI links', { error: error.message });
      return [];
    }
  },

  /**
   * Get NPI link for a specific user
   * @param {string} userId - User ID
   * @returns {Object|null} NPI link or null
   */
  getUserNPILink(userId) {
    try {
      const links = this.getAllLinks();
      return links.find(link => link.userId === userId) || null;
    } catch (error) {
      logger.error('Error getting user NPI link', { error: error.message });
      return null;
    }
  },

  /**
   * Get user ID linked to a specific NPI number
   * @param {string} npi - NPI number
   * @returns {string|null} User ID or null
   */
  getNPIOwner(npi) {
    try {
      const links = this.getAllLinks();
      const link = links.find(link => link.npi === npi && link.status === 'verified');
      return link ? link.userId : null;
    } catch (error) {
      logger.error('Error getting NPI owner', { error: error.message });
      return null;
    }
  },

  /**
   * Search for NPI profile to link
   * @param {string} searchQuery - Name or NPI to search
   * @param {string} specialty - Provider specialty for validation
   * @returns {Promise<Array>} Matching NPI profiles
   */
  async searchNPIForLinking(searchQuery, specialty = '') {
    try {
      // First try exact NPI number search
      if (/^\d{10}$/.test(searchQuery.replace(/\D/g, ''))) {
        const npiNumber = searchQuery.replace(/\D/g, '');
        const exactMatch = await NPIAPI.getProviderByNPI(npiNumber);
        if (exactMatch) {
          return [exactMatch];
        }
      }

      // Otherwise search by name
      const searchResults = await NPIAPI.searchProviders({
        name: searchQuery,
        specialty: specialty || 'all',
        limit: 10
      });

      return searchResults.providers || [];
    } catch (error) {
      logger.error('Error searching NPI for linking', { error: error.message });
      throw new Error('Failed to search NPI registry');
    }
  },

  /**
   * Initiate NPI verification process
   * @param {string} userId - User ID
   * @param {Object} userProfile - User profile data
   * @param {string} npi - NPI number to verify
   * @param {Object} npiProfile - NPI profile data
   * @returns {Object} Verification result
   */
  async initiateNPIVerification(userId, userProfile, npi, npiProfile) {
    try {
      // Check if NPI is already claimed
      const existingOwner = this.getNPIOwner(npi);
      if (existingOwner && existingOwner !== userId) {
        throw new Error('This NPI is already verified by another user');
      }

      // Validate matching criteria
      const validationResults = this.validateNPIMatch(userProfile, npiProfile);
      
      if (!validationResults.isValidMatch) {
        throw new Error(`Verification failed: ${validationResults.reasons.join(', ')}`);
      }

      // Create pending verification link
      const verification = {
        id: Date.now().toString(),
        userId,
        npi,
        npiProfile,
        userProfile,
        status: 'pending',
        validationScore: validationResults.score,
        validationDetails: validationResults,
        initiatedAt: new Date().toISOString(),
        expiresAt: new Date(Date.now() + 24 * 60 * 60 * 1000).toISOString(), // 24 hours
        verificationMethod: 'profile_match'
      };

      // Save verification attempt
      this.saveVerificationAttempt(verification);

      // If validation score is high enough, auto-verify
      if (validationResults.score >= 0.8) {
        return this.completeNPIVerification(verification.id, {
          method: 'auto_verification',
          score: validationResults.score
        });
      }

      // Otherwise, require manual verification steps
      return {
        success: false,
        requiresVerification: true,
        verificationId: verification.id,
        validationScore: validationResults.score,
        suggestions: this.getVerificationSuggestions(validationResults)
      };

    } catch (error) {
      logger.error('Error initiating NPI verification', { error: error.message });
      throw error;
    }
  },

  /**
   * Validate if user profile matches NPI profile
   * @param {Object} userProfile - User profile data
   * @param {Object} npiProfile - NPI profile data
   * @returns {Object} Validation results
   */
  validateNPIMatch(userProfile, npiProfile) {
    const results = {
      isValidMatch: false,
      score: 0,
      reasons: [],
      details: {}
    };

    let totalScore = 0;
    let maxScore = 0;

    // Name matching (40% weight)
    maxScore += 40;
    const nameMatch = this.calculateNameMatch(userProfile.fullName, npiProfile.name);
    totalScore += nameMatch.score * 40;
    results.details.nameMatch = nameMatch;
    
    if (nameMatch.score < 0.6) {
      results.reasons.push('Name does not match NPI profile');
    }

    // Specialty matching (30% weight)
    maxScore += 30;
    const specialtyMatch = this.calculateSpecialtyMatch(userProfile.specialty, npiProfile.specialty);
    totalScore += specialtyMatch.score * 30;
    results.details.specialtyMatch = specialtyMatch;
    
    if (specialtyMatch.score < 0.7) {
      results.reasons.push('Specialty does not match NPI profile');
    }

    // Location proximity (20% weight) - if user provided practice location
    if (userProfile.practiceAddress && npiProfile.address) {
      maxScore += 20;
      const locationMatch = this.calculateLocationMatch(userProfile.practiceAddress, npiProfile.address);
      totalScore += locationMatch.score * 20;
      results.details.locationMatch = locationMatch;
      
      if (locationMatch.score < 0.5) {
        results.reasons.push('Practice location does not match NPI profile');
      }
    }

    // Email domain matching (10% weight) - if email suggests practice affiliation
    if (userProfile.email && userProfile.email.includes('.')) {
      maxScore += 10;
      const emailMatch = this.calculateEmailMatch(userProfile.email, npiProfile);
      totalScore += emailMatch.score * 10;
      results.details.emailMatch = emailMatch;
    }

    results.score = maxScore > 0 ? totalScore / maxScore : 0;
    results.isValidMatch = results.score >= 0.6 && results.reasons.length === 0;

    return results;
  },

  /**
   * Calculate name matching score
   * @param {string} userName - User's name
   * @param {string} npiName - NPI profile name
   * @returns {Object} Name match results
   */
  calculateNameMatch(userName, npiName) {
    if (!userName || !npiName) {
      return { score: 0, details: 'Missing name data' };
    }

    // Normalize names
    const normalizeNamee = (name) => {
      return name.toLowerCase()
        .replace(/^dr\.?\s*/, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const userNormalized = normalizeNamee(userName);
    const npiNormalized = normalizeNamee(npiName);

    // Exact match
    if (userNormalized === npiNormalized) {
      return { score: 1.0, details: 'Exact match' };
    }

    // Extract first and last names
    const userParts = userNormalized.split(' ');
    const npiParts = npiNormalized.split(' ');

    if (userParts.length < 2 || npiParts.length < 2) {
      return { score: 0.3, details: 'Insufficient name components' };
    }

    const userFirst = userParts[0];
    const userLast = userParts[userParts.length - 1];
    const npiFirst = npiParts[0];
    const npiLast = npiParts[npiParts.length - 1];

    // Check if first and last names match
    if (userFirst === npiFirst && userLast === npiLast) {
      return { score: 0.9, details: 'First and last name match' };
    }

    // Check if last names match and first names are similar
    if (userLast === npiLast) {
      if (userFirst.startsWith(npiFirst) || npiFirst.startsWith(userFirst)) {
        return { score: 0.8, details: 'Last name exact, first name partial' };
      }
      return { score: 0.7, details: 'Last name match only' };
    }

    return { score: 0.2, details: 'Names do not match sufficiently' };
  },

  /**
   * Calculate specialty matching score
   * @param {string} userSpecialty - User's specialty
   * @param {string} npiSpecialty - NPI profile specialty
   * @returns {Object} Specialty match results
   */
  calculateSpecialtyMatch(userSpecialty, npiSpecialty) {
    if (!userSpecialty || !npiSpecialty) {
      return { score: 0.5, details: 'Missing specialty data' };
    }

    const normalizeSpecialty = (specialty) => {
      return specialty.toLowerCase()
        .replace(/[^a-z\s]/g, '')
        .replace(/\s+/g, ' ')
        .trim();
    };

    const userNorm = normalizeSpecialty(userSpecialty);
    const npiNorm = normalizeSpecialty(npiSpecialty);

    // Exact match
    if (userNorm === npiNorm) {
      return { score: 1.0, details: 'Exact specialty match' };
    }

    // Check for substring matches
    if (userNorm.includes(npiNorm) || npiNorm.includes(userNorm)) {
      return { score: 0.8, details: 'Specialty substring match' };
    }

    // Check for common specialty variations
    const specialtyMappings = {
      'family medicine': ['family practice', 'family physician'],
      'internal medicine': ['internist', 'internal med'],
      'emergency medicine': ['emergency', 'er physician'],
      'orthopedic surgery': ['orthopedics', 'orthopaedic surgery'],
      'cardiovascular disease': ['cardiology', 'heart specialist']
    };

    for (const [canonical, variations] of Object.entries(specialtyMappings)) {
      if ((userNorm.includes(canonical) || variations.some(v => userNorm.includes(v))) &&
          (npiNorm.includes(canonical) || variations.some(v => npiNorm.includes(v)))) {
        return { score: 0.9, details: 'Specialty variation match' };
      }
    }

    return { score: 0.3, details: 'Specialties do not match' };
  },

  /**
   * Calculate location matching score
   * @param {string} userAddress - User's practice address
   * @param {string} npiAddress - NPI profile address
   * @returns {Object} Location match results
   */
  calculateLocationMatch(userAddress, npiAddress) {
    if (!userAddress || !npiAddress) {
      return { score: 0.5, details: 'Missing address data' };
    }

    // Extract city and state from addresses
    const extractLocation = (address) => {
      const parts = address.split(',').map(p => p.trim());
      if (parts.length >= 2) {
        const cityState = parts[parts.length - 2];
        const stateZip = parts[parts.length - 1];
        const state = stateZip.split(' ')[0];
        return { city: cityState.toLowerCase(), state: state.toLowerCase() };
      }
      return null;
    };

    const userLoc = extractLocation(userAddress);
    const npiLoc = extractLocation(npiAddress);

    if (!userLoc || !npiLoc) {
      return { score: 0.3, details: 'Could not parse location data' };
    }

    // Same city and state
    if (userLoc.city === npiLoc.city && userLoc.state === npiLoc.state) {
      return { score: 1.0, details: 'Same city and state' };
    }

    // Same state
    if (userLoc.state === npiLoc.state) {
      return { score: 0.7, details: 'Same state' };
    }

    return { score: 0.2, details: 'Different locations' };
  },

  /**
   * Calculate email matching score
   * @param {string} userEmail - User's email
   * @param {Object} npiProfile - NPI profile data
   * @returns {Object} Email match results
   */
  calculateEmailMatch(userEmail, npiProfile) {
    if (!userEmail) {
      return { score: 0.5, details: 'No email provided' };
    }

    const domain = userEmail.split('@')[1]?.toLowerCase();
    if (!domain) {
      return { score: 0.3, details: 'Invalid email format' };
    }

    // Check if email domain suggests medical practice
    const medicalDomains = ['gmail.com', 'yahoo.com', 'hotmail.com', 'outlook.com'];
    if (medicalDomains.includes(domain)) {
      return { score: 0.4, details: 'Personal email domain' };
    }

    // Professional domain suggests legitimacy
    return { score: 0.8, details: 'Professional email domain' };
  },

  /**
   * Get verification suggestions based on validation results
   * @param {Object} validationResults - Validation results
   * @returns {Array} Verification suggestions
   */
  getVerificationSuggestions(validationResults) {
    const suggestions = [];

    if (validationResults.details.nameMatch?.score < 0.7) {
      suggestions.push({
        type: 'name_verification',
        message: 'Verify your name matches the NPI profile exactly',
        action: 'Update profile name or provide documentation'
      });
    }

    if (validationResults.details.specialtyMatch?.score < 0.7) {
      suggestions.push({
        type: 'specialty_verification',
        message: 'Confirm your specialty matches the NPI registration',
        action: 'Update specialty or provide board certification'
      });
    }

    if (validationResults.details.locationMatch?.score < 0.5) {
      suggestions.push({
        type: 'location_verification',
        message: 'Verify your practice location',
        action: 'Provide current practice address or employment verification'
      });
    }

    return suggestions;
  },

  /**
   * Complete NPI verification
   * @param {string} verificationId - Verification ID
   * @param {Object} verificationData - Additional verification data
   * @returns {Object} Completion result
   */
  async completeNPIVerification(verificationId, verificationData = {}) {
    try {
      const attempts = this.getAllVerificationAttempts();
      const attemptIndex = attempts.findIndex(a => a.id === verificationId);

      if (attemptIndex === -1) {
        throw new Error('Verification attempt not found');
      }

      const attempt = attempts[attemptIndex];

      if (attempt.status !== 'pending') {
        throw new Error('Verification already processed');
      }

      if (new Date(attempt.expiresAt) < new Date()) {
        throw new Error('Verification attempt expired');
      }

      // Create verified NPI link
      const npiLink = {
        id: Date.now().toString(),
        userId: attempt.userId,
        npi: attempt.npi,
        npiProfile: attempt.npiProfile,
        status: 'verified',
        verifiedAt: new Date().toISOString(),
        verificationMethod: verificationData.method || 'manual',
        validationScore: attempt.validationScore,
        verificationData
      };

      // Save NPI link to localStorage
      const links = this.getAllLinks();
      links.push(npiLink);
      localStorage.setItem(NPI_LINKS_KEY, JSON.stringify(links));

      // Update Supabase user profile with NPI data
      logger.debug('Syncing NPI data to Supabase profile', {
        userId: attempt.userId,
        npi: attempt.npi
      });
      await this.mergeNPIDataToProfile(attempt.userId, attempt.npiProfile);

      // Update verification attempt status
      attempts[attemptIndex].status = 'completed';
      attempts[attemptIndex].completedAt = new Date().toISOString();
      localStorage.setItem(VERIFICATION_ATTEMPTS_KEY, JSON.stringify(attempts));

      return {
        success: true,
        npiLink,
        message: 'NPI profile successfully linked and verified'
      };

    } catch (error) {
      logger.error('Error completing NPI verification', { error: error.message });
      throw error;
    }
  },

  /**
   * Save verification attempt
   * @param {Object} attempt - Verification attempt data
   */
  saveVerificationAttempt(attempt) {
    try {
      const attempts = this.getAllVerificationAttempts();
      attempts.push(attempt);
      localStorage.setItem(VERIFICATION_ATTEMPTS_KEY, JSON.stringify(attempts));
    } catch (error) {
      logger.error('Error saving verification attempt', { error: error.message });
    }
  },

  /**
   * Get all verification attempts
   * @returns {Array} Verification attempts
   */
  getAllVerificationAttempts() {
    try {
      const attempts = localStorage.getItem(VERIFICATION_ATTEMPTS_KEY);
      return attempts ? JSON.parse(attempts) : [];
    } catch (error) {
      logger.error('Error getting verification attempts', { error: error.message });
      return [];
    }
  },

  /**
   * Get verification attempt by ID
   * @param {string} verificationId - Verification ID
   * @returns {Object|null} Verification attempt
   */
  getVerificationAttempt(verificationId) {
    try {
      const attempts = this.getAllVerificationAttempts();
      return attempts.find(a => a.id === verificationId) || null;
    } catch (error) {
      logger.error('Error getting verification attempt', { error: error.message });
      return null;
    }
  },

  /**
   * Unlink NPI profile (admin function)
   * @param {string} userId - User ID
   * @returns {boolean} Success status
   */
  unlinkNPI(userId) {
    try {
      const links = this.getAllLinks();
      const filteredLinks = links.filter(link => link.userId !== userId);
      localStorage.setItem(NPI_LINKS_KEY, JSON.stringify(filteredLinks));
      return true;
    } catch (error) {
      logger.error('Error unlinking NPI', { error: error.message });
      return false;
    }
  },

  /**
   * Get statistics for NPI linking
   * @returns {Object} Statistics
   */
  getLinkingStats() {
    try {
      const links = this.getAllLinks();
      const attempts = this.getAllVerificationAttempts();

      return {
        totalLinked: links.length,
        totalAttempts: attempts.length,
        verificationSuccessRate: attempts.length > 0 ?
          (links.length / attempts.length * 100).toFixed(1) + '%' : '0%',
        byVerificationMethod: links.reduce((acc, link) => {
          acc[link.verificationMethod] = (acc[link.verificationMethod] || 0) + 1;
          return acc;
        }, {}),
        averageValidationScore: links.length > 0 ?
          (links.reduce((sum, link) => sum + link.validationScore, 0) / links.length).toFixed(2) : 0
      };
    } catch (error) {
      logger.error('Error getting linking stats', { error: error.message });
      return null;
    }
  },

  /**
   * Merge NPI data into Supabase user profile
   * Called after successful NPI verification to enrich profile with verified data
   * @param {string} userId - User ID
   * @param {Object} npiProfile - NPI profile data from registry
   * @returns {Promise<Object>} Updated profile data or null
   */
  async mergeNPIDataToProfile(userId, npiProfile) {
    try {
      logger.debug('Merging NPI data to Supabase profile', {
        userId,
        npi: npiProfile.npi,
        specialty: npiProfile.specialty
      });

      // Import Supabase dynamically to avoid circular dependencies
      const { supabase } = await import('./supabase');

      if (!supabase) {
        logger.warn('Supabase not available, NPI data will not sync to profile');
        return null;
      }

      // Prepare profile update with NPI data
      const profileUpdate = {
        npi_number: npiProfile.npi,
        specialty: npiProfile.specialty,
        company: npiProfile.hospital || npiProfile.organization || null,
        phone: npiProfile.phone || null,
        location: {
          address: npiProfile.address,
          city: npiProfile.city,
          state: npiProfile.state,
          zip: npiProfile.zip || npiProfile.postal_code || ''
        }
      };

      logger.debug('Profile update payload', profileUpdate);

      // Update user_profiles table
      const { data, error } = await supabase
        .from('user_profiles')
        .update(profileUpdate)
        .eq('id', userId)
        .select()
        .single();

      if (error) {
        logger.error('Error updating profile with NPI data', {
          error: error.message,
          code: error.code,
          userId
        });
        throw error;
      }

      logger.debug('Successfully merged NPI data to profile', {
        userId,
        npi: npiProfile.npi,
        updatedFields: Object.keys(profileUpdate)
      });

      return data;

    } catch (error) {
      logger.error('Failed to merge NPI data to profile', {
        error: error.message,
        userId
      });
      // Don't throw - we still want NPI linking to succeed even if Supabase update fails
      // The data is in localStorage and can be synced later
      return null;
    }
  }
};

export default NPILinking;